/**
 * NoteBlock.js
 *
 * Two display modes:
 *
 * 1. COMPACT — small tile pinned onto the miniature A4 paper grid.
 *    Clicking calls `onExpand(note)` — handled by A4Sheet.
 *
 * 2. EXPANDED — full-A4 lightbox view of a single note.
 *    Rendered by A4Sheet via a portal-style overlay.
 *    Shows all content at comfortable reading sizes with an
 *    inline-editable form and a Save / Cancel / Delete action bar.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './NoteBlock.module.css';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Compact tile (inside miniature A4 paper)                                   */
/* ─────────────────────────────────────────────────────────────────────────── */
export function CompactNoteBlock({ note, onExpand, onDelete, isLanding = false, layoutIdPrefix = '' }) {
  const dateStr = note.created_at
    ? new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <motion.div
      className={styles.noteBlock}
      layoutId={`${layoutIdPrefix}note-tile-${note.id}`}
      initial={isLanding ? { scale: 0.35, opacity: 0 } : { opacity: 0, y: 6 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={
        isLanding
          ? { type: 'spring', stiffness: 380, damping: 24, delay: 0.05 }
          : { duration: 0.22, ease: 'easeOut' }
      }
      onClick={(e) => { e.stopPropagation(); onExpand?.(note); }}
    >
      {/* Delete shortcut (no expand) */}
      <button
        className={`${styles.noteDeleteBtn} no-print`}
        data-no-print="true"
        onClick={e => { e.stopPropagation(); onDelete?.(note.id); }}
        title="Delete note"
      >
        ✕
      </button>

      <p className={styles.noteTopic}>{note.topic}</p>
      <p className={styles.noteSummary}>{note.summary}</p>

      <div className={styles.noteMeta}>
        <span className={styles.noteCategoryTag}><span className={styles.noteCategoryText}>{note.category || 'Note'}</span></span>
        <span className={styles.noteDate}>{dateStr}</span>
      </div>

      <span className={styles.expandHint}>↗ expand</span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Expanded note overlay (full-A4 canvas view)                                */
/* ─────────────────────────────────────────────────────────────────────────── */
export function ExpandedNoteBlock({ note, pageIndex, onSave, onDelete, onClose, noLayoutId = false, isUnlocked = false, onLockedAction, subjectTitle = 'Study' }) {
  const [editing, setEditing] = useState(false);
  const [editTopic,    setEditTopic]    = useState(note.topic);
  const [editSummary,  setEditSummary]  = useState(note.summary);
  const [editDetail,   setEditDetail]   = useState(note.detail   || '');
  const [editCategory, setEditCategory] = useState(note.category || 'Note');

  // Re-sync whenever the selected note changes
  useEffect(() => {
    setEditTopic(note.topic);
    setEditSummary(note.summary);
    setEditDetail(note.detail || '');
    setEditCategory(note.category || 'Note');
    setEditing(false);
  }, [note.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const beginEditing = () => {
    if (!isUnlocked) return onLockedAction?.();
    setEditing(true);
  };

  const handleSave = () => {
    if (!isUnlocked) return onLockedAction?.();
    onSave?.(note.id, {
      topic:    editTopic.trim()    || note.topic,
      summary:  editSummary.trim()  || note.summary,
      detail:   editDetail.trim(),
      category: editCategory.trim() || note.category,
    });
    setEditing(false);
    onClose();
  };

  const handleDelete = () => {
    if (!isUnlocked) return onLockedAction?.();
    onDelete?.(note.id);
    onClose();
  };

  const dateStr = note.created_at
    ? new Date(note.created_at).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '';

  return (
    <motion.div
      className={styles.expandedOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
    >
      <motion.div
        className={styles.expandedPaper}
        {...(!noLayoutId && { layoutId: `note-tile-${note.id}` })}
        initial={{ scale: 0.88, opacity: 0, y: 16 }}
        animate={{ scale: 1,    opacity: 1, y: 0  }}
        exit={{ scale: 0.92,    opacity: 0, y: 8  }}
        transition={{ type: 'spring', stiffness: 360, damping: 30 }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Action bar ─────────────────────────────────────────────── */}
        <div className={styles.expandedActionBar}>
          <div className={styles.expandedActionLeft}>
            <span className={styles.expandedPageLabel}>
              {subjectTitle} · Page {pageIndex + 1}
            </span>
          </div>
          <div className={styles.expandedActionRight}>
            <button className={styles.btnBack} onClick={onClose}>
              ← Back
            </button>
            <button className={styles.btnDelete} onClick={handleDelete}>
              🗑 Delete
            </button>
            <button className={styles.btnSave} onClick={handleSave}>
              ✓ Save & Close
            </button>
          </div>
        </div>

        {/* ── Content body ───────────────────────────────────────────── */}
        <div className={styles.expandedBody}>
          <AnimatePresence mode="wait">
            {!editing ? (
              /* ── READ mode ─────────────────────────────────────────── */
              <motion.div
                key="read"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                {/* Meta */}
                <div className={styles.expandedMeta}>
                  <span className={styles.expandedCategoryTag}>
                    {note.category || 'Note'}
                  </span>
                  <span className={styles.expandedDate}>{dateStr}</span>
                </div>

                <div className={styles.expandedDivider} />

                {/* Topic */}
                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>Topic</span>
                  <p
                    className={styles.readTopic}
                    onClick={beginEditing}
                    title="Click to edit"
                  >
                    {editTopic}
                  </p>
                </div>

                {/* Summary */}
                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>Summary</span>
                  <p
                    className={styles.readSummary}
                    onClick={beginEditing}
                    title="Click to edit"
                  >
                    {editSummary}
                  </p>
                </div>

                {/* Detail / Code */}
                {editDetail && (
                  <div className={styles.fieldGroup}>
                    <span className={styles.fieldLabel}>Detail / Code</span>
                    <pre
                      className={styles.readDetail}
                      onClick={beginEditing}
                      title="Click to edit"
                    >
                      {editDetail}
                    </pre>
                  </div>
                )}

                <p className={styles.clickToEditHint}>
                  {isUnlocked ? 'Click any field to edit ✎' : 'Unlock operations to edit or delete this note.'}
                </p>
              </motion.div>
            ) : (
              /* ── EDIT mode ─────────────────────────────────────────── */
              <motion.div
                key="edit"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {/* Category */}
                <div className={styles.fieldGroup}>
                  <label className={styles.editLabel}>Category</label>
                  <input
                    className={`${styles.editInput} ${styles.editInputCategory}`}
                    value={editCategory}
                    onChange={e => setEditCategory(e.target.value)}
                  />
                </div>

                <div className={styles.expandedDivider} />

                {/* Topic */}
                <div className={styles.fieldGroup}>
                  <label className={styles.editLabel}>Topic</label>
                  <input
                    className={`${styles.editInput} ${styles.editInputTopic}`}
                    value={editTopic}
                    onChange={e => setEditTopic(e.target.value)}
                    autoFocus
                  />
                </div>

                {/* Summary */}
                <div className={styles.fieldGroup}>
                  <label className={styles.editLabel}>Summary</label>
                  <textarea
                    className={`${styles.editInput} ${styles.editInputSummary}`}
                    value={editSummary}
                    onChange={e => setEditSummary(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Detail / Code */}
                <div className={styles.fieldGroup}>
                  <label className={styles.editLabel}>Detail / Code</label>
                  <textarea
                    className={`${styles.editInput} ${styles.editInputCode}`}
                    value={editDetail}
                    onChange={e => setEditDetail(e.target.value)}
                    rows={6}
                    placeholder="Optional: add code snippet or detailed explanation..."
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Default export: compact (backward-compat) ────────────────────────── */
export default CompactNoteBlock;
